import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Loader2 } from "lucide-react";
import { Reveal } from "../components/site/Reveal";
import { useContent } from "../context/ContentContext";
import { img } from "../lib/api";

export default function ProductDetail() {
  const { slug } = useParams();
  const { products, loading } = useContent();

  const product = products.find((p) => p.slug === slug || p.id === slug);

  if (loading && !product) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-volt" size={28} />
      </div>
    );
  }

  if (!product) {
    return (
      <section className="mx-auto flex min-h-[70vh] max-w-[1400px] flex-col items-center justify-center px-6 text-center">
        <h1 className="font-display text-4xl font-medium">Product not found</h1>
        <Link to="/products" className="brand-gradient mt-8 inline-flex rounded-full px-7 py-3.5 font-semibold text-white">Back to products</Link>
      </section>
    );
  }

  const paragraphs = (product.long || product.desc || "").split("\n\n").filter(Boolean);
  const features = product.features || [];

  return (
    <article className="mx-auto max-w-[1400px] px-6 pb-28 pt-32 md:px-10 md:pt-40" data-testid="product-detail">
      <Link to="/products" data-testid="back-to-products" className="inline-flex items-center gap-2 text-sm font-medium text-white/50 transition-colors hover:text-white">
        <ArrowLeft size={16} /> All products
      </Link>

      <div className="mt-10 grid gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-white/10">
            <img src={img(product.image)} alt={product.name} className="aspect-[4/3] w-full object-cover" />
            <span className="absolute left-5 top-5 rounded-full border border-white/20 bg-black/40 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white/80 backdrop-blur">
              {product.category}
            </span>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <div className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-volt">{product.category}</div>
            <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">{product.name}</h1>
            <p className="mt-4 text-xl text-gradient">{product.desc}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 space-y-4">
              {paragraphs.map((p, i) => (
                <p key={i} className="text-lg leading-relaxed text-white/70">{p}</p>
              ))}
            </div>
          </Reveal>

          {features.length > 0 && (
            <Reveal delay={0.15}>
              <div className="mt-10">
                <div className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-white/40">What's inside</div>
                <ul className="grid gap-3 sm:grid-cols-2">
                  {features.map((f) => (
                    <li key={f} className="flex items-center gap-3 rounded-xl border border-white/10 bg-korio-surface px-4 py-3 text-white/80">
                      <Check size={18} className="flex-shrink-0 text-volt" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}

          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to={`/contact?product=${encodeURIComponent(product.name)}`} data-testid="product-get-quote"
                className="brand-gradient group inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">
                Get a quote <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/products" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 font-semibold text-white transition-colors hover:bg-white/5">
                More products
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </article>
  );
}
